// .olm file open/save. An .olm is a thin wrapper around the Fabric v7 canvas JSON:
//   { app:'olm', version, size:{width,height,name}mm, orientation, canvas:<fabric toObject> }
// Open also accepts a raw Fabric JSON ({version, objects}) for convenience.
import { getCanvas, state } from './state'
import { resizeCanvas, fitToView, labelMm, refreshGrid } from './canvas'
import { migrateDesignJSON } from './migration'
import { resetHistory } from './history'
import { updateLayers } from './panels/layers'
import { onSelectionChange } from './panels/properties'
import { updateStatus } from './statusbar'
import { refreshHeader } from './ui/toolbar'
import { getPages, setPages, initPages } from './pages'
import { resolveCatalogueId } from './constants'
import { detectBatchFields } from './batch/engine'

export async function openOlmDesign(file: File): Promise<void> {
  await loadOlmJson(JSON.parse(await file.text()))
}

/**
 * Classic-editor design files ("Save design" wrote `*.olm.json`) and classic
 * autosaves have a different wrapper than .olm:
 *   { version:1, tool:'OpenLabelMaker', size:{id,w,h,name,brand}, orientation,
 *     canvas:<fabric json>, batchHeaders?, batchData? }   (autosave uses `data`)
 * Sizes are landscape-canonical w/h (mm) + an orientation flag. Convert to the
 * .olm v1 shape loadOlmJson already understands. Returns null if not classic.
 */
export function fromClassicDesign(data: any): any | null {
  if (!data || data.app === 'olm' || Array.isArray(data.pages)) return null
  const canvas = data.canvas ?? data.data
  if (!canvas || typeof canvas !== 'object' || !data.size || !('w' in data.size)) return null
  const portrait = data.orientation === 'portrait'
  const w = Number(data.size.w) || 0
  const h = Number(data.size.h) || 0
  return {
    app: 'olm', version: 1,
    size: w && h ? { width: portrait ? h : w, height: portrait ? w : h, name: data.size.name } : undefined,
    legacySizeId: typeof data.size.id === 'string' ? data.size.id : undefined,
    canvas,
    batchHeaders: Array.isArray(data.batchHeaders) ? data.batchHeaders : undefined,
    batchData: Array.isArray(data.batchData) ? data.batchData : undefined,
  }
}

/** Load an already-parsed .olm object (classic design file, or raw Fabric JSON)
 * into the canvas. Shared by file-open, autosave restore and the AI import. */
export async function loadOlmJson(data: any): Promise<void> {
  const classic = fromClassicDesign(data)
  if (classic) data = classic
  const wrapped = data && data.app === 'olm'
  const canvasJson = wrapped ? data.canvas : data
  const size = wrapped ? data.size : undefined

  // Catalogue identity first: v2 sheetId / sizeId, or the classic file's size id
  // (legacySizeId, incl. classic 'sheet-<id>' forms). Restoring the real entry
  // keeps brand/shape/category, the "Change size" UI and — for sheets — the
  // N-up export, instead of degrading to a custom one-off size.
  const hit = wrapped
    ? (data.sheetId ? resolveCatalogueId(data.sheetId) : undefined)
      ?? resolveCatalogueId(data.sizeId ?? data.legacySizeId)
    : undefined
  if (hit && size?.width && size?.height) {
    state.currentSheet = hit.sheet
    state.currentSize = hit.size
    state.orientation = size.height > size.width ? 'portrait' : 'landscape'
    resizeCanvas()
  } else if (size?.width && size?.height) {
    state.currentSheet = null
    // `size` is the TRUE display W×H. LABEL_SIZES store landscape-canonical dims and
    // 'portrait' swaps them (labelMm/sizeLabelText). So store landscape-canonical here and
    // set orientation from the aspect, so a tall A4 reads as Portrait — not Landscape.
    const portrait = size.height > size.width
    state.orientation = portrait ? 'portrait' : 'landscape'
    state.currentSize = {
      id: 'imported',
      name: size.name ?? pageName(size.width, size.height),
      brand: '',
      widthMM: portrait ? size.height : size.width,
      heightMM: portrait ? size.width : size.height,
      shape: 'rect', category: 'custom',
    }
    resizeCanvas()
  }
  const c = getCanvas()

  // .olm v2 — multi-page document. Each page is its own canvas_json (same size).
  if (Array.isArray(data.pages)) {
    const prepared = data.pages.map((p: any, i: number) => ({
      name: p.name || `Page ${i + 1}`,
      canvas: migrateDesignJSON(expandDataRegions(p.canvas ?? p)),
    }))
    await setPages(prepared) // loads page 1 onto the canvas
    if (!c.backgroundColor) c.backgroundColor = '#ffffff'
    c.requestRenderAll()
    fitToView(); updateLayers(); onSelectionChange(); updateStatus(); refreshHeader(); resetHistory()
    return
  }

  // v1 / raw single canvas.
  await c.loadFromJSON(migrateDesignJSON(expandDataRegions(canvasJson)))
  // loadFromJSON clears backgroundColor when the design has none — default to white so a
  // page (e.g. an imported A4 invoice) reads as a real page, not the editor's grid.
  if (!c.backgroundColor) c.backgroundColor = '#ffffff'
  refreshGrid() // loadFromJSON wiped the grid overlay
  c.requestRenderAll()
  fitToView(); updateLayers(); onSelectionChange(); updateStatus()
  refreshHeader() // sync the top size label + orientation buttons to the loaded size
  initPages() // snapshot the loaded single design as page 1
  resetHistory() // make the loaded design the undo baseline (carries its background)
  // Classic files may embed their batch dataset — restore it so fields resolve.
  if (wrapped && data.batchHeaders && data.batchData) {
    state.batchHeaders = data.batchHeaders
    state.batchData = data.batchData
    state.batchFields = detectBatchFields()
    document.dispatchEvent(new CustomEvent('olm:batchchange'))
  }
}

/** Name a common page size from its dimensions (mm, either orientation); else "Imported". */
function pageName(w: number, h: number): string {
  const lo = Math.min(w, h), hi = Math.max(w, h)
  const near = (a: number, b: number) => Math.abs(a - b) <= 2
  if (near(lo, 210) && near(hi, 297)) return 'A4'
  if (near(lo, 148) && near(hi, 210)) return 'A5'
  if (near(lo, 297) && near(hi, 420)) return 'A3'
  if (near(lo, 215.9) && near(hi, 279.4)) return 'Letter'
  if (near(lo, 215.9) && near(hi, 355.6)) return 'Legal'
  return 'Imported'
}

/** Turn AI-import 'dataregion' markers (which Fabric can't enliven) into a flagged
 * dashed placeholder + label. The user converts it to a real DataTable in the panel. */
function expandDataRegions(canvasJson: any): any {
  if (!canvasJson || !Array.isArray(canvasJson.objects)) return canvasJson
  const objects: any[] = []
  for (const o of canvasJson.objects) {
    if (o && o.type === 'dataregion') {
      const left = o.left ?? 40, top = o.top ?? 60, width = o.width ?? 360, height = o.height ?? 120
      const regionId = o.regionId ?? `region-${objects.length}`
      const columns = Array.isArray(o.columns) ? o.columns : []
      objects.push({
        type: 'rect', left, top, width, height, originX: 'left', originY: 'top',
        fill: 'rgba(59,130,246,0.06)', stroke: '#3b82f6', strokeWidth: 1.5, strokeDashArray: [6, 4], rx: 4,
        olmDataRegion: { columns, regionId },
      })
      objects.push({
        type: 'textbox',
        text: `▦ Data table — ${columns.length} column${columns.length === 1 ? '' : 's'} detected. Select this box, then “Convert to data table”.`,
        left: left + 8, top: top + 8, width: Math.max(80, width - 16),
        fontSize: 12, fill: '#2563eb', fontFamily: 'Inter', originX: 'left', originY: 'top',
        olmDataRegionLabel: regionId,
      })
    } else {
      objects.push(o)
    }
  }
  return { ...canvasJson, objects }
}

/** Build the .olm v2 wrapper (all pages) without downloading — used by save + tests. */
export function saveOlmObject(): any {
  const { w, h } = labelMm() // true display dims (handles any current orientation)
  return {
    app: 'olm', version: 2,
    size: { width: w, height: h, name: state.currentSize.name },
    // Catalogue identity (additive; older readers ignore): the exact size entry
    // and, for multi-up sheets, the sheet — so a reload gets the N-up export back.
    sizeId: state.currentSize.id,
    sheetId: state.currentSheet?.id ?? null,
    pages: getPages().map((p) => ({ name: p.name, canvas: p.json })),
  }
}

export function saveOlmDesign(filename = 'design.olm'): void {
  const olm = saveOlmObject()
  const blob = new Blob([JSON.stringify(olm)], { type: 'application/json' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}
